import { useEffect, useState } from "react";
import { Investigation } from "../../lusca-search-api/generated-src";

const SEEN_KEY = "lusca.trackstarsRunSeen";
/** Runs older than this never count as new (e.g. results from before this badge existed). */
const NEW_FOR_MS = 7 * 24 * 60 * 60 * 1000;
/** Keep the map small: only the most recently seen investigations are remembered. */
const MAX_REMEMBERED = 200;

const readSeen = (): Record<string, string> => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(SEEN_KEY) || "{}"
    ) as unknown;
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
};

const writeSeen = (investigationId: string, runId: string) => {
  try {
    const seen = readSeen();
    delete seen[investigationId];
    seen[investigationId] = runId;
    const ids = Object.keys(seen);
    ids
      .slice(0, Math.max(0, ids.length - MAX_REMEMBERED))
      .forEach((id) => delete seen[id]);
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // Storage full or blocked: the badge just shows again next visit.
  }
};

/**
 * Whether the case's latest completed TrackStars run is one this browser has not looked at
 * yet (the TrackStars tab label shows "New"). Opening the tab marks the run seen.
 */
export const useNewTrackStarsRun = (
  investigation: Pick<Investigation, "id" | "trackstarsRun"> | undefined,
  tabOpen: boolean,
  now: number = Date.now()
): boolean => {
  const run = investigation?.trackstarsRun;
  const completedRunId =
    run?.status === "COMPLETED" &&
    now - Date.parse(run.completedAt ?? run.requestedAt) < NEW_FOR_MS
      ? run.runId
      : undefined;
  const [seenRunId, setSeenRunId] = useState<string | undefined>(() =>
    investigation ? readSeen()[investigation.id] : undefined
  );
  useEffect(() => {
    if (investigation) setSeenRunId(readSeen()[investigation.id]);
  }, [investigation?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      investigation &&
      completedRunId &&
      tabOpen &&
      seenRunId !== completedRunId
    ) {
      writeSeen(investigation.id, completedRunId);
      setSeenRunId(completedRunId);
    }
  }, [investigation, completedRunId, tabOpen, seenRunId]);
  return !!completedRunId && !tabOpen && seenRunId !== completedRunId;
};
